/**
 * Client-side copies of server “one aspect per day” rows: build placeholder days for a
 * block’s date span for optimistic UI, and merge with prior rows after resize so stable
 * ids (and worker_assignment) survive when the span still includes that calendar day.
 */
import {
  isLocalWeekend,
  toLocalCalendarDay,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import { getAspectBlockId } from "./scheduleBlockSelectors";
import { sameScheduleBlockId } from "./scheduleIds";

/**
 * Crew list for new per-day rows: mirrors block-level `worker_assignment` (shallow copy).
 *
 * @param {Object} block
 * @returns {Array<Object>}
 */
export function defaultWorkerAssignmentFromBlock(block) {
  if (!Array.isArray(block?.worker_assignment) || block.worker_assignment.length === 0) {
    return [];
  }
  return block.worker_assignment.map((w) => ({ ...w }));
}

/** @param {Object} block In-memory block with `startDate` / `endDate`. */
export function materializeAspectDaysForBlock(block) {
  const start = toLocalCalendarDay(block.startDate);
  const end = toLocalCalendarDay(block.endDate);

  const days = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const key = toLocalDateKey(cursor);
    const workingDaysOnly = Boolean(block.workingDaysOnly);
    days.push({
      id: `${block.id}-day-${key}`,
      dayPosition: 0,
      workScopeAspectsBlock: { id: block.id },
      date: new Date(cursor),
      worker_assignment: defaultWorkerAssignmentFromBlock(block),
      skipped: workingDaysOnly && isLocalWeekend(cursor),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/**
 * In-memory placeholder aspect for a new block date.
 *
 * @param {Object} block
 * @param {Date} date
 * @param {{ skipped?: boolean }} [options]
 * @returns {Object}
 */
function buildPlaceholderAspectForDate(block, date, options = {}) {
  const key = toLocalDateKey(date);
  const workingDaysOnly = Boolean(block.workingDaysOnly);
  return {
    id: `${block.id}-day-${key}`,
    dayPosition: 0,
    workScopeAspectsBlock: { id: block.id },
    date,
    worker_assignment: defaultWorkerAssignmentFromBlock(block),
    skipped: options.skipped ?? (workingDaysOnly && isLocalWeekend(date)),
  };
}

/**
 * Hidden-weekend drag filler: weekend days become skipped rows, and carried work rows
 * continue on the next visible weekday so the bar keeps the same weekday width.
 *
 * @param {Object} block
 * @param {Array<Object>} previousAspectsForBlock
 * @returns {{ nextBlock: Object, nextAspects: Array<Object> }}
 */
export function materializeHiddenWeekendDragMove(block, previousAspectsForBlock) {
  const carriedAspects = [...previousAspectsForBlock]
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .filter((aspect) => !(Boolean(aspect.skipped) && isLocalWeekend(aspect.date)));
  if (carriedAspects.length === 0) {
    return { nextBlock: block, nextAspects: [] };
  }
  const nextAspects = [];
  const cursor = toLocalCalendarDay(block.startDate);
  let aspectIndex = 0;

  while (aspectIndex < carriedAspects.length) {
    const date = new Date(cursor);
    if (isLocalWeekend(date)) {
      nextAspects.push(buildPlaceholderAspectForDate(block, date, { skipped: true }));
    } else {
      nextAspects.push({
        ...carriedAspects[aspectIndex],
        date,
        workScopeAspectsBlock: { id: block.id },
      });
      aspectIndex += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  const lastAspect = nextAspects[nextAspects.length - 1];
  return {
    nextBlock: lastAspect ? { ...block, endDate: lastAspect.date } : block,
    nextAspects,
  };
}

/**
 * Pair sorted aspect rows into sorted new dates, optionally keeping skipped weekend
 * rows attached to the same weekend day when weekends are hidden.
 *
 * @param {Object} block
 * @param {Array<Object>} prevSorted
 * @param {Array<Date>} newDays
 * @param {boolean} preserveSkippedWeekendsByWeekday
 * @returns {Array<Object>}
 */
function pairAspectRowsBySlot(
  block,
  prevSorted,
  newDays,
  preserveSkippedWeekendsByWeekday
) {
  if (!preserveSkippedWeekendsByWeekday) {
    return newDays.map((date, i) => ({
      ...prevSorted[i],
      date,
      workScopeAspectsBlock: { id: block.id },
    }));
  }

  const paired = new Array(newDays.length);
  const usedAspectIndexes = new Set();
  const usedDateIndexes = new Set();

  for (const weekday of [6, 0]) {
    const skippedWeekendAspectIndexes = prevSorted
      .map((aspect, index) => ({ aspect, index }))
      .filter(
        ({ aspect }) =>
          Boolean(aspect.skipped) &&
          isLocalWeekend(aspect.date) &&
          toLocalCalendarDay(aspect.date).getDay() === weekday
      )
      .map(({ index }) => index);
    const matchingDateIndexes = newDays
      .map((date, index) => ({ date, index }))
      .filter(({ date }) => date.getDay() === weekday)
      .map(({ index }) => index);

    const pairCount = Math.min(
      skippedWeekendAspectIndexes.length,
      matchingDateIndexes.length
    );
    for (let i = 0; i < pairCount; i += 1) {
      const aspectIndex = skippedWeekendAspectIndexes[i];
      const dateIndex = matchingDateIndexes[i];
      paired[dateIndex] = {
        ...prevSorted[aspectIndex],
        date: newDays[dateIndex],
        workScopeAspectsBlock: { id: block.id },
      };
      usedAspectIndexes.add(aspectIndex);
      usedDateIndexes.add(dateIndex);
    }
  }

  const remainingAspects = prevSorted.filter(
    (_aspect, index) => !usedAspectIndexes.has(index)
  );
  let nextAspectIndex = 0;
  for (let dateIndex = 0; dateIndex < newDays.length; dateIndex += 1) {
    if (usedDateIndexes.has(dateIndex)) {
      continue;
    }
    const aspect = remainingAspects[nextAspectIndex];
    paired[dateIndex] = aspect
      ? {
        ...aspect,
        date: newDays[dateIndex],
        workScopeAspectsBlock: { id: block.id },
      }
      : buildPlaceholderAspectForDate(block, newDays[dateIndex]);
    nextAspectIndex += 1;
  }

  return paired;
}

/**
 * After block start/end changes: build one in-memory row per day in the new span.
 *
 * - **Calendar match (default):** each new day reuses the prior row for that same
 *   calendar date when it exists; otherwise a placeholder. Use for **resize** so per-day
 *   overrides stay on the same dates.
 * - **Slot match (`pairBySortedIndexOnEqualLength`):** when the new span has the same
 *   day count as before, pair old and new days by **sorted index** so the i-th day of
 *   the block keeps the i-th aspect row. Use for **drag-move** where the whole span
 *   shifts but length is unchanged.
 *
 * @param {Object} block
 * @param {Array<Object>} previousAspectsForBlock
 * @param {{ pairBySortedIndexOnEqualLength?: boolean, preserveSkippedWeekendsByWeekday?: boolean, skipNewWeekendAspectDays?: boolean }} [options]
 * @returns {Array<Object>}
 */
export function mergeAspectDaysAfterBlockSpanChange(
  block,
  previousAspectsForBlock,
  options = {}
) {
  const {
    pairBySortedIndexOnEqualLength = false,
    preserveSkippedWeekendsByWeekday = false,
    skipNewWeekendAspectDays = false,
  } = options;

  const start = toLocalCalendarDay(block.startDate);
  const end = toLocalCalendarDay(block.endDate);
  const newDays = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    newDays.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  const prevSorted = [...previousAspectsForBlock].sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );

  if (newDays.length === prevSorted.length && pairBySortedIndexOnEqualLength) {
    return pairAspectRowsBySlot(
      block,
      prevSorted,
      newDays,
      preserveSkippedWeekendsByWeekday
    );
  }

  const prevByKey = new Map(
    prevSorted.map((d) => [toLocalDateKey(d.date), d])
  );
  return newDays.map((date) => {
    const key = toLocalDateKey(date);
    const prev = prevByKey.get(key);
    if (prev) {
      return {
        ...prev,
        date,
        workScopeAspectsBlock: { id: block.id },
      };
    }
    return buildPlaceholderAspectForDate(block, date, {
      skipped: skipNewWeekendAspectDays && isLocalWeekend(date),
    });
  });
}

/**
 * Returns the full flat WorkScopeAspect list after replacing one block’s rows: removes
 * every per-day row owned by `blockId`, then appends `replacementAspects`. Matches the
 * usual state update
 * `setWorkScopeAspects((prev) => [...prev.filter(!this block), ...nextFor])`.
 *
 * @param {Array<Object>} allAspects Current list (e.g. `workScopeAspectsRef.current`)
 * @param {unknown} blockId Block whose aspect rows are replaced
 * @param {Array<Object>} replacementAspects New rows for that block only (e.g. `nextFor` from {@link mergeAspectDaysAfterBlockSpanChange})
 * @returns {Array<Object>}
 */
export function replaceBlockAspectRowsInFlatList(
  allAspects,
  blockId,
  replacementAspects
) {
  return [
    ...allAspects.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
    ...replacementAspects,
  ];
}
